<template>
    <div class="block-editor provider-response" v-if="clone">
        <div class="wrapper">
            <!-- Channel Selection -->
            <section v-if="openrouterChannels.length > 0">
                <div class="title">{{ $t("Kanal Seçin (Opsiyonel)") }}</div>
                <a-select
                    v-model="clone.channelId"
                    style="width: 100%"
                    :placeholder="$t('Otomatik kanal seçimi')"
                    allowClear
                >
                    <a-select-option v-for="channel in openrouterChannels" :key="channel._id" :value="channel._id">
                        {{ channel.name }}
                    </a-select-option>
                </a-select>
            </section>

            <!-- Model Selection -->
            <section>
                <div class="title">{{ $t("Model") }}</div>
                <div class="model-input-wrapper">
                    <TiptapEditor
                        :placeholder="getModelPlaceholder()"
                        :show-attributes="true"
                        v-model="clone.model"
                        automation-mode
                        class="model-input"
                    />
                    <a-button
                        type="primary"
                        @click="openModelSelector"
                        :loading="loadingModels"
                        :disabled="!selectedChannel"
                    >
                        <span class="material-icons" v-if="!loadingModels">search</span>
                        {{ $t("Model Seç") }}
                    </a-button>
                </div>
                <div class="help-text">
                    <i class="material-icons">info</i>
                    <span>{{ modelHint }}</span>
                </div>
            </section>

            <!-- Messages Array -->
            <section>
                <div class="title">
                    {{ $t("Mesajlar") }}
                    <a-button size="small" type="primary" ghost @click="addMessage">
                        <span class="material-icons" style="font-size: 16px">add</span>
                        {{ $t("Mesaj Ekle") }}
                    </a-button>
                </div>

                <div class="messages-list">
                    <div v-for="(msg, index) in clone.messages" :key="'msg-' + index" class="message-item">
                        <div class="message-header">
                            <a-select v-model="msg.role" style="width: 140px">
                                <a-select-option value="user">User</a-select-option>
                                <a-select-option value="system">System</a-select-option>
                                <a-select-option value="assistant">Assistant</a-select-option>
                                <a-select-option value="tool">Tool</a-select-option>
                            </a-select>
                            <div class="message-actions">
                                <a-button v-if="index > 0" size="small" type="link" @click="moveMessage(index, -1)">
                                    <span class="material-icons">arrow_upward</span>
                                </a-button>
                                <a-button
                                    v-if="index < clone.messages.length - 1"
                                    size="small"
                                    type="link"
                                    @click="moveMessage(index, 1)"
                                >
                                    <span class="material-icons">arrow_downward</span>
                                </a-button>
                                <a-button
                                    size="small"
                                    type="link"
                                    @click="removeMessage(index)"
                                    :disabled="clone.messages.length <= 1"
                                >
                                    <span class="material-icons">delete</span>
                                </a-button>
                            </div>
                        </div>

                        <!-- Tool specific fields -->
                        <div v-if="msg.role === 'tool'" class="tool-fields">
                            <TiptapEditor
                                v-model="msg.toolCallId"
                                :placeholder="$t('Tool Call ID')"
                                :show-attributes="true"
                                automation-mode
                                class="tool-field-input"
                            />
                            <TiptapEditor
                                v-model="msg.name"
                                :placeholder="$t('Tool Name')"
                                :show-attributes="true"
                                automation-mode
                                class="tool-field-input"
                            />
                        </div>

                        <!-- Content Type Selector -->
                        <div class="content-type-section">
                            <a-select
                                v-model="msg.contentType"
                                style="width: 160px; margin-bottom: 8px"
                                :placeholder="$t('İçerik Tipi')"
                                @change="onContentTypeChange(msg, $event)"
                            >
                                <a-select-option value="text">
                                    <span class="material-icons" style="font-size: 14px; vertical-align: middle"
                                        >text_fields</span
                                    >
                                    Metin
                                </a-select-option>
                                <a-select-option value="image">
                                    <span class="material-icons" style="font-size: 14px; vertical-align: middle"
                                        >image</span
                                    >
                                    Resim (URL)
                                </a-select-option>
                                <a-select-option value="file">
                                    <span class="material-icons" style="font-size: 14px; vertical-align: middle"
                                        >attach_file</span
                                    >
                                    Dosya
                                </a-select-option>
                                <a-select-option value="audio">
                                    <span class="material-icons" style="font-size: 14px; vertical-align: middle"
                                        >audiotrack</span
                                    >
                                    Ses
                                </a-select-option>
                                <a-select-option value="video">
                                    <span class="material-icons" style="font-size: 14px; vertical-align: middle"
                                        >videocam</span
                                    >
                                    Video
                                </a-select-option>
                            </a-select>

                            <!-- Text Content -->
                            <div v-if="!msg.contentType || msg.contentType === 'text'">
                                <TiptapEditor
                                    :show-emoji="true"
                                    :show-attributes="true"
                                    :placeholder="getMessagePlaceholder(msg.role)"
                                    :max-character-count="10000"
                                    v-model="msg.content"
                                    :key="'msg-content-' + index + '-' + renderKey"
                                    automation-mode
                                />
                            </div>

                            <!-- Image Content -->
                            <div v-else-if="msg.contentType === 'image'" class="media-input">
                                <TiptapEditor
                                    :show-attributes="true"
                                    :placeholder="$t('Resim URL veya Base64 girin')"
                                    v-model="msg.imageUrl"
                                    automation-mode
                                />
                                <a-select v-model="msg.imageDetail" style="width: 120px; margin-top: 8px">
                                    <a-select-option value="auto">Auto</a-select-option>
                                    <a-select-option value="high">High</a-select-option>
                                    <a-select-option value="low">Low</a-select-option>
                                </a-select>
                            </div>

                            <!-- File Content -->
                            <div v-else-if="msg.contentType === 'file'" class="media-input">
                                <TiptapEditor
                                    :show-attributes="true"
                                    :placeholder="$t('Dosya URL girin')"
                                    v-model="msg.fileUrl"
                                    automation-mode
                                />
                                <TiptapEditor
                                    :show-attributes="true"
                                    :placeholder="$t('Dosya adı (opsiyonel)')"
                                    v-model="msg.filename"
                                    automation-mode
                                    style="margin-top: 8px"
                                />
                            </div>

                            <!-- Audio Content -->
                            <div v-else-if="msg.contentType === 'audio'" class="media-input">
                                <TiptapEditor
                                    :show-attributes="true"
                                    :placeholder="$t('Ses URL veya Base64 girin')"
                                    v-model="msg.audioUrl"
                                    automation-mode
                                />
                            </div>

                            <!-- Video Content -->
                            <div v-else-if="msg.contentType === 'video'" class="media-input">
                                <TiptapEditor
                                    :show-attributes="true"
                                    :placeholder="$t('Video URL girin')"
                                    v-model="msg.videoUrl"
                                    automation-mode
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Tools Section -->
            <section>
                <div class="title">
                    {{ $t("Tools / Functions") }}
                    <a-button size="small" type="primary" ghost @click="addTool">
                        <span class="material-icons" style="font-size: 16px">add</span>
                        {{ $t("Tool Ekle") }}
                    </a-button>
                </div>

                <div v-if="clone.tools && clone.tools.length > 0" class="tools-list">
                    <div v-for="(tool, tIndex) in clone.tools" :key="'tool-' + tIndex" class="tool-item">
                        <div class="tool-header">
                            <span class="tool-badge">function</span>
                            <div class="tool-name-wrapper">
                                <label>{{ $t("Fonksiyon Adı") }}</label>
                                <a-input v-model="tool.function.name" :placeholder="$t('get_weather')" />
                            </div>
                            <a-button size="small" type="link" danger @click="removeTool(tIndex)">
                                <span class="material-icons">delete</span>
                            </a-button>
                        </div>

                        <div class="tool-description-wrapper">
                            <label>{{ $t("Açıklama") }}</label>
                            <TiptapEditor
                                v-model="tool.function.description"
                                :placeholder="$t('Bu fonksiyon ne yapar?')"
                                :show-attributes="true"
                                automation-mode
                            />
                        </div>

                        <!-- Parameters Section -->
                        <div class="tool-parameters">
                            <div class="parameters-header">
                                <label>{{ $t("Parametreler") }}</label>
                                <a-button size="small" type="dashed" @click="addToolParameter(tool)">
                                    <span class="material-icons" style="font-size: 14px">add</span>
                                    {{ $t("Parametre Ekle") }}
                                </a-button>
                            </div>

                            <div
                                v-if="tool.function.parameters && tool.function.parameters.length > 0"
                                class="parameters-list"
                            >
                                <div
                                    v-for="(param, pIndex) in tool.function.parameters"
                                    :key="'param-' + tIndex + '-' + pIndex"
                                    class="parameter-item"
                                >
                                    <div class="param-field type-field">
                                        <label>{{ $t("Tip") }}</label>
                                        <a-select v-model="param.type" style="width: 100%">
                                            <a-select-option value="string">String</a-select-option>
                                            <a-select-option value="number">Number</a-select-option>
                                            <a-select-option value="boolean">Boolean</a-select-option>
                                            <a-select-option value="array">Array</a-select-option>
                                            <a-select-option value="object">Object</a-select-option>
                                        </a-select>
                                    </div>
                                    <div class="param-field name-field">
                                        <label>{{ $t("İsim") }}</label>
                                        <a-input v-model="param.name" :placeholder="$t('param_name')" />
                                    </div>
                                    <div class="param-field description-field">
                                        <label>{{ $t("Açıklama") }}</label>
                                        <a-input
                                            v-model="param.description"
                                            :placeholder="$t('Parametre açıklaması')"
                                        />
                                    </div>
                                    <div class="param-field required-field">
                                        <label>{{ $t("Zorunlu") }}</label>
                                        <a-switch v-model="param.required" size="small" />
                                    </div>
                                    <a-button
                                        size="small"
                                        type="link"
                                        danger
                                        @click="removeToolParameter(tool, pIndex)"
                                        class="delete-param-btn"
                                    >
                                        <span class="material-icons">delete</span>
                                    </a-button>
                                </div>
                            </div>
                            <div v-else class="no-parameters">
                                <span>{{ $t("Henüz parametre eklenmedi") }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Tool Choice -->
                    <div class="tool-choice-section">
                        <label>{{ $t("Tool Choice") }}</label>
                        <a-select v-model="clone.tool_choice" style="width: 100%">
                            <a-select-option value="auto">Auto</a-select-option>
                            <a-select-option value="none">None</a-select-option>
                            <a-select-option value="required">Required</a-select-option>
                        </a-select>
                    </div>
                </div>
                <div v-else class="empty-tools">
                    <span class="material-icons">build</span>
                    <span>{{ $t("Henüz tool eklenmedi") }}</span>
                </div>
            </section>

            <!-- Structured Output Section -->
            <section>
                <div class="title">
                    {{ $t("Structured Output (Opsiyonel)") }}
                    <a-switch v-model="clone.useStructuredOutput" size="small" />
                </div>

                <div v-if="clone.useStructuredOutput" class="structured-output-section">
                    <TiptapEditor
                        v-model="clone.structuredOutput.name"
                        :placeholder="$t('Schema adı (örn: weather, user_info)')"
                        :show-attributes="true"
                        automation-mode
                        class="schema-name-input"
                    />
                    <a-textarea
                        v-model="clone.structuredOutput.schemaJson"
                        :placeholder="schemaJsonPlaceholder"
                        :auto-size="{ minRows: 4, maxRows: 12 }"
                        class="schema-json-input"
                    />
                    <div class="strict-mode">
                        <a-checkbox v-model="clone.structuredOutput.strict">
                            {{ $t("Strict Mode") }}
                        </a-checkbox>
                    </div>
                    <div class="help-text">
                        <i class="material-icons">info</i>
                        <span
                            >Structured output, modelin yanıtını belirli bir JSON Schema formatında döndürmesini zorunlu
                            kılar.</span
                        >
                    </div>
                </div>
            </section>

            <!-- Parameters Section -->
            <section>
                <div class="title">{{ $t("Parametreler") }}</div>

                <div class="params-grid">
                    <!-- Temperature -->
                    <div class="param-item">
                        <label>
                            {{ $t("Temperature") }}
                            <span class="param-value">{{ clone.temperature }}</span>
                        </label>
                        <a-slider v-model="clone.temperature" :min="0" :max="2" :step="0.1" />
                    </div>

                    <!-- Top P -->
                    <div class="param-item">
                        <label>
                            {{ $t("Top P") }}
                            <span class="param-value">{{ clone.top_p }}</span>
                        </label>
                        <a-slider v-model="clone.top_p" :min="0" :max="1" :step="0.05" />
                    </div>

                    <!-- Max Tokens -->
                    <div class="param-item full-width">
                        <label>{{ $t("Max Tokens") }}</label>
                        <a-input-number
                            v-model="clone.maxTokens"
                            :min="1"
                            :max="128000"
                            style="width: 100%"
                            :placeholder="$t('Varsayılan: Model limiti')"
                        />
                    </div>
                </div>
            </section>
        </div>

        <!-- Model Selector Modal -->
        <a-modal v-model="modelSelectorVisible" :title="modelModalTitle" width="900px" :footer="null">
            <div class="model-selector-modal">
                <div class="models-info">
                    <a-tag color="purple">{{ providerLabel }}</a-tag>
                    <span class="models-count">{{ openRouterModels.length }} model bulundu</span>
                </div>
                <a-input-search
                    v-model="modelSearchQuery"
                    :placeholder="$t('Model ara...')"
                    style="margin-bottom: 16px"
                />
                <a-table
                    :columns="modelColumns"
                    :data-source="filteredModels"
                    :pagination="{ pageSize: 10 }"
                    :scroll="{ y: 400 }"
                    size="small"
                    row-key="id"
                >
                    <template slot="action" slot-scope="text, record">
                        <a-button type="primary" size="small" @click="selectModel(record)">
                            <a-icon type="check" /> Seç
                        </a-button>
                    </template>
                </a-table>
            </div>
        </a-modal>
    </div>
</template>

<script>
import _ from "lodash";
import TiptapEditor from "../../../../../Chatbot/pages/dashboard/common/TiptapEditor.vue";
import { sendSocketMessage, socketMessageStream } from "../../../../../../../utils/socketStream";

var PROVIDER_LABELS = {
    openrouter: "OpenRouter",
    openai: "OpenAI",
    google: "Google AI",
    anthropic: "Anthropic",
    mistral: "Mistral",
    azure: "Azure",
    bedrock: "AWS Bedrock",
    cohere: "Cohere",
    xai: "xAI",
    deepinfra: "DeepInfra",
    together: "Together",
    fireworks: "Fireworks",
    siliconflow: "SiliconFlow",
    parasail: "Parasail",
    nebius: "Nebius",
    alibaba: "Alibaba",
    xiaomi: "Xiaomi",
    sambanova: "SambaNova",
    moonshot: "Moonshot",
    minimax: "MiniMax",
    nvidia: "NVIDIA",
};

var MODEL_EXAMPLES = {
    openrouter: "openai/gpt-4o veya google/gemini-2.0-flash-001",
    openai: "openai/gpt-4o",
    google: "google/gemini-2.0-flash-001",
    anthropic: "anthropic/claude-3.5-sonnet",
    mistral: "mistralai/mistral-large",
    azure: "azure/gpt-4o",
    bedrock: "amazon/titan-text-express-v1",
    cohere: "cohere/command-r-plus",
    xai: "x-ai/grok-2",
    deepinfra: "meta-llama/Llama-3.3-70B-Instruct",
    together: "meta-llama/Llama-3.3-70B-Instruct-Turbo",
    fireworks: "accounts/fireworks/models/llama-v3p3-70b-instruct",
    siliconflow: "deepseek-ai/DeepSeek-V3",
    parasail: "parasail/llama-3.1-405b-instruct",
    nebius: "Qwen/Qwen2.5-72B-Instruct",
    alibaba: "qwen/qwen-turbo",
    xiaomi: "xiaomi/MiMo-7B-RL",
    sambanova: "Meta-Llama-3.1-405B-Instruct",
    moonshot: "moonshot-v1-8k",
    minimax: "minimax/minimax-01",
    nvidia: "nvidia/llama-3.1-nemotron-70b-instruct",
};

export default {
    name: "ProviderResponse",
    components: { TiptapEditor },
    props: {
        value: {
            type: Object,
            default: function () {
                return {};
            },
        },
        provider: {
            type: String,
            default: "openrouter",
        },
    },
    data: function () {
        var defaultValue = {
            channelId: null,
            model: "",
            messages: [{ role: "user", content: "", contentType: "text" }],
            tools: [],
            temperature: 0.7,
            top_p: 0.9,
            tool_choice: "auto",
            maxTokens: null,
            useStructuredOutput: false,
            structuredOutput: {
                name: "",
                schemaJson:
                    '{\n  "type": "object",\n  "properties": {},\n  "required": [],\n  "additionalProperties": false\n}',
                strict: true,
            },
        };
        return {
            clone: _.merge({}, defaultValue, _.cloneDeep(this.value)),
            renderKey: 0,
            modelSelectorVisible: false,
            modelSearchQuery: "",
            loadingModels: false,
            openRouterModels: [],
            socketSubscription: null,
            modelColumns: [
                { title: "Model ID", dataIndex: "id", key: "id", width: "300px", ellipsis: true },
                { title: "İsim", dataIndex: "name", key: "name", ellipsis: true },
                { title: "Context", dataIndex: "context_length", key: "context_length", width: "100px" },
                { title: "İşlem", key: "action", scopedSlots: { customRender: "action" }, width: "100px" },
            ],
        };
    },
    computed: {
        channels: function () {
            return this.$store.state.channel.channels;
        },
        openrouterChannels: function () {
            var self = this;
            return this.channels.filter(function (ch) {
                if (ch.type !== "openrouter" || ch.isDeleted || !ch.enabled) {
                    return false;
                }
                // Provider'a göre filtrele
                var channelProvider = ch.payload && ch.payload.provider ? ch.payload.provider : "openrouter";
                // OpenRouter seçiliyse tüm kanalları göster
                if (self.provider === "openrouter") {
                    return true;
                }
                // Diğer provider'lar için sadece o provider'a ait kanalları göster
                return channelProvider === self.provider || channelProvider === "openrouter";
            });
        },
        selectedChannel: function () {
            var self = this;
            if (this.clone.channelId) {
                return this.openrouterChannels.find(function (ch) {
                    return ch._id === self.clone.channelId;
                });
            }
            // Return first available channel if none selected
            return this.openrouterChannels[0] || null;
        },
        providerLabel: function () {
            return PROVIDER_LABELS[this.provider] || this.provider;
        },
        modelHint: function () {
            var example = MODEL_EXAMPLES[this.provider] || MODEL_EXAMPLES.openrouter;
            return "Örn: " + example;
        },
        filteredModels: function () {
            var query = this.modelSearchQuery.toLowerCase();
            if (!query) {
                return this.openRouterModels;
            }
            return this.openRouterModels.filter(function (model) {
                return (
                    (model.name && model.name.toLowerCase().indexOf(query) !== -1) ||
                    (model.id && model.id.toLowerCase().indexOf(query) !== -1)
                );
            });
        },
        schemaJsonPlaceholder: function () {
            return 'JSON Schema (örn: {"type": "object", "properties": {...}, "required": [...]})';
        },
        modelModalTitle: function () {
            var channelName = this.selectedChannel ? this.selectedChannel.name : "";
            return channelName ? channelName + " - Model Listesi" : "Model Listesi";
        },
    },
    watch: {
        clone: {
            handler: function (newValue) {
                this.$emit("input", newValue);
            },
            deep: true,
        },
        value: {
            handler: function (newValue) {
                if (!_.isEqual(newValue, this.clone)) {
                    this.clone = _.cloneDeep(newValue);
                }
            },
            deep: true,
        },
    },
    methods: {
        getModelPlaceholder: function () {
            var example = MODEL_EXAMPLES[this.provider] || MODEL_EXAMPLES.openrouter;
            return "Örn: " + example;
        },
        getMessagePlaceholder: function (role) {
            var placeholders = {
                user: "Kullanıcı mesajını yazın...",
                system: "Sistem talimatlarını yazın...",
                assistant: "Asistan cevabını yazın...",
                tool: "Tool cevabını yazın...",
            };
            return placeholders[role] || "Mesaj içeriği...";
        },
        addMessage: function () {
            if (!this.clone.messages) {
                this.clone.messages = [];
            }
            this.clone.messages.push({
                role: "user",
                content: "",
                contentType: "text",
            });
        },
        removeMessage: function (index) {
            if (this.clone.messages.length > 1) {
                this.clone.messages.splice(index, 1);
            }
        },
        moveMessage: function (index, direction) {
            var newIndex = index + direction;
            if (newIndex >= 0 && newIndex < this.clone.messages.length) {
                var messages = this.clone.messages;
                var temp = messages[index];
                this.$set(messages, index, messages[newIndex]);
                this.$set(messages, newIndex, temp);
                this.renderKey++;
            }
        },
        onContentTypeChange: function (msg, type) {
            msg.content = "";
            msg.imageUrl = "";
            msg.imageDetail = "auto";
            msg.fileUrl = "";
            msg.filename = "";
            msg.audioUrl = "";
            msg.videoUrl = "";
        },
        addTool: function () {
            if (!this.clone.tools) {
                this.clone.tools = [];
            }
            this.clone.tools.push({
                type: "function",
                function: {
                    name: "",
                    description: "",
                    parameters: [],
                },
            });
        },
        removeTool: function (index) {
            this.clone.tools.splice(index, 1);
        },
        addToolParameter: function (tool) {
            if (!tool.function.parameters) {
                tool.function.parameters = [];
            }
            tool.function.parameters.push({
                name: "",
                description: "",
                type: "string",
                required: true,
            });
        },
        removeToolParameter: function (tool, index) {
            tool.function.parameters.splice(index, 1);
        },
        openModelSelector: function () {
            if (!this.selectedChannel) {
                this.$message.warning("Lütfen önce bir kanal seçin");
                return;
            }
            this.loadingModels = true;
            this.modelSearchQuery = "";

            sendSocketMessage("user openrouter-list-models", {
                siteId: this.$store.state.site.site._id,
                userId: this.$store.state.auth.user._id,
                channelId: this.selectedChannel._id,
            });
        },
        handleOpenRouterModelsResult: function (data) {
            this.loadingModels = false;
            if (data.success) {
                this.openRouterModels = data.models || [];
                this.modelSelectorVisible = true;
            } else {
                this.$message.error(data.message || "Model listesi alınamadı");
            }
        },
        selectModel: function (model) {
            this.clone.model = model.id;
            this.modelSelectorVisible = false;
        },
    },
    created: function () {
        // Ensure messages array exists
        if (!this.clone.messages || !Array.isArray(this.clone.messages) || this.clone.messages.length === 0) {
            this.clone.messages = [{ role: "user", content: "", contentType: "text" }];
        }
        // Ensure tools array exists
        if (!this.clone.tools) {
            this.clone.tools = [];
        }
        // Ensure structured output object exists
        if (!this.clone.structuredOutput) {
            this.clone.structuredOutput = {
                name: "",
                schemaJson:
                    '{\n  "type": "object",\n  "properties": {},\n  "required": [],\n  "additionalProperties": false\n}',
                strict: true,
            };
        }
        // Ensure contentType for existing messages
        this.clone.messages.forEach(function (msg) {
            if (!msg.contentType) {
                msg.contentType = "text";
            }
        });
        // Migrate old tool format to new parameters array format
        var self = this;
        if (this.clone.tools && this.clone.tools.length > 0) {
            this.clone.tools.forEach(function (tool) {
                if (tool.function && tool.function.parametersJson && !tool.function.parameters) {
                    // Try to migrate from JSON to parameters array
                    try {
                        var parsed = JSON.parse(tool.function.parametersJson);
                        if (parsed.properties) {
                            tool.function.parameters = [];
                            var required = parsed.required || [];
                            Object.keys(parsed.properties).forEach(function (key) {
                                var prop = parsed.properties[key];
                                tool.function.parameters.push({
                                    name: key,
                                    description: prop.description || "",
                                    type: prop.type || "string",
                                    required: required.indexOf(key) !== -1,
                                });
                            });
                        }
                    } catch (e) {
                        tool.function.parameters = [];
                    }
                }
                if (!tool.function.parameters) {
                    tool.function.parameters = [];
                }
            });
        }
    },
    mounted: function () {
        var self = this;
        this.socketSubscription = socketMessageStream.subscribe(function (message) {
            if (message.cmd === "openrouter_list_models_result") {
                self.handleOpenRouterModelsResult(message.data);
            }
        });
    },
    beforeDestroy: function () {
        if (this.socketSubscription) {
            this.socketSubscription.unsubscribe();
        }
    },
};
</script>

<style lang="scss">
.block-editor.provider-response {
    .wrapper {
        padding: 30px;

        section {
            margin-bottom: 24px;

            .title {
                display: flex;
                align-items: center;
                justify-content: space-between;
                margin-bottom: 12px;
                font-weight: 600;
                font-size: 16px;
                line-height: 20px;
            }

            .help-text {
                display: flex;
                align-items: flex-start;
                gap: 8px;
                margin-top: 8px;
                padding: 10px;
                background: #f0f8ff;
                border-radius: 6px;
                font-size: 13px;
                color: #666;

                .material-icons {
                    font-size: 18px;
                    color: #1890ff;
                }
            }
        }
    }

    .model-input-wrapper {
        display: flex;
        gap: 10px;
        align-items: flex-start;

        .model-input {
            flex: 1;
        }

        .ant-btn {
            display: flex;
            align-items: center;
            gap: 4px;
            height: 38px;

            .material-icons {
                font-size: 18px;
            }
        }
    }

    .messages-list {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .message-item {
        border: 1px solid #e8e8e8;
        border-radius: 8px;
        padding: 16px;
        background: #fafafa;

        .message-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 12px;
        }

        .message-actions {
            display: flex;
            align-items: center;
            gap: 2px;

            .ant-btn {
                padding: 2px 6px;
                .material-icons {
                    font-size: 18px;
                }
            }
        }

        .tool-fields {
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin-bottom: 12px;
        }

        .content-type-section {
            .media-input {
                margin-top: 8px;
            }
        }
    }

    .tools-list {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .tool-item {
        border: 1px solid #e8e8e8;
        border-radius: 8px;
        padding: 16px;
        background: #fafafa;

        .tool-header {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            margin-bottom: 16px;

            .tool-name-wrapper {
                flex: 1;

                label {
                    display: block;
                    margin-bottom: 4px;
                    font-weight: 500;
                    font-size: 13px;
                    color: #666;
                }
            }
        }

        .tool-badge {
            background: #667eea;
            color: white;
            padding: 4px 10px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;
            margin-top: 22px;
        }

        .tool-description-wrapper {
            margin-bottom: 16px;

            label {
                display: block;
                margin-bottom: 4px;
                font-weight: 500;
                font-size: 13px;
                color: #666;
            }
        }

        .tool-parameters {
            border-top: 1px solid #e8e8e8;
            padding-top: 16px;

            .parameters-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                margin-bottom: 12px;

                label {
                    font-weight: 500;
                    font-size: 14px;
                    color: #333;
                }
            }

            .parameters-list {
                display: flex;
                flex-direction: column;
                gap: 12px;
            }

            .parameter-item {
                display: flex;
                align-items: flex-end;
                gap: 10px;
                padding: 12px;
                background: #fff;
                border: 1px solid #e8e8e8;
                border-radius: 6px;

                .param-field {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;

                    label {
                        font-size: 12px;
                        color: #888;
                        font-weight: 500;
                    }

                    &.type-field {
                        width: 120px;
                    }

                    &.name-field {
                        width: 150px;
                    }

                    &.description-field {
                        flex: 1;
                    }

                    &.required-field {
                        width: 60px;
                        align-items: center;
                    }
                }

                .delete-param-btn {
                    margin-bottom: 4px;
                }
            }

            .no-parameters {
                padding: 16px;
                text-align: center;
                color: #999;
                background: #fff;
                border-radius: 6px;
                border: 1px dashed #d9d9d9;
            }
        }
    }

    .tool-choice-section {
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid #e8e8e8;

        label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
        }
    }

    .empty-tools {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 24px;
        background: #fafafa;
        border-radius: 8px;
        color: #999;

        .material-icons {
            font-size: 20px;
        }
    }

    .structured-output-section {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 16px;
        background: #fafafa;
        border-radius: 8px;
        border: 1px solid #e8e8e8;

        .schema-json-input {
            font-family: monospace;
            font-size: 13px;
        }

        .strict-mode {
            margin-top: 8px;
        }
    }

    .params-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 20px;

        .param-item {
            &.full-width {
                grid-column: 1 / -1;
            }

            label {
                display: flex;
                align-items: center;
                justify-content: space-between;
                margin-bottom: 8px;
                font-weight: 500;

                .param-value {
                    font-weight: normal;
                    color: #667eea;
                    font-size: 13px;
                }
            }
        }
    }

    .model-selector-modal {
        .models-info {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 16px;

            .models-count {
                font-size: 13px;
                color: #666;
            }
        }
    }
}
</style>
